import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/useAuthStore'
import { useI18n } from '../i18n'

type Section = 'profile' | 'email' | 'password' | 'address'

export function AccountPage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const error = useAuthStore((s) => s.error)
  const clearError = useAuthStore((s) => s.clearError)
  const updateEmail = useAuthStore((s) => s.updateEmail)
  const updatePassword = useAuthStore((s) => s.updatePassword)
  const updateProfile = useAuthStore((s) => s.updateProfile)

  const [section, setSection] = useState<Section>('profile')
  const [success, setSuccess] = useState('')

  // Email
  const [newEmail, setNewEmail] = useState('')

  // Password
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')

  // Address
  const [street, setStreet] = useState(user?.street ?? '')
  const [zip, setZip] = useState(user?.zip ?? '')
  const [city, setCity] = useState(user?.city ?? '')
  const [country, setCountry] = useState(user?.country ?? '')

  if (!user) {
    navigate('/login')
    return null
  }

  const switchSection = (s: Section) => {
    setSection(s)
    setSuccess('')
    clearError()
  }

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setSuccess('')
    const ok = await updateEmail(newEmail)
    if (ok) {
      setSuccess(t('account.emailConfirmation', { email: newEmail }))
      setNewEmail('')
    }
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setSuccess('')
    if (newPassword !== confirmNewPassword) return
    const ok = await updatePassword(currentPassword, newPassword)
    if (ok) {
      setSuccess(t('account.passwordChanged'))
      setCurrentPassword('')
      setNewPassword('')
      setConfirmNewPassword('')
    }
  }

  const handleAddressChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setSuccess('')
    const ok = await updateProfile({ street, zip, city, country })
    if (ok) setSuccess(t('account.saved'))
  }

  const sectionBtn = (s: Section, label: string) => (
    <button
      onClick={() => switchSection(s)}
      className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
        section === s
          ? 'bg-primary text-white'
          : 'bg-surface-lighter text-text-muted hover:text-text'
      }`}
    >
      {label}
    </button>
  )

  const createdDate = new Date(user.createdAt).toLocaleDateString()

  return (
    <div className="mx-auto w-full max-w-lg px-4 py-12">
      <button
        onClick={() => navigate('/')}
        className="mb-6 flex items-center gap-1 text-sm text-text-muted hover:text-primary transition-colors"
      >
        <span>←</span> Home
      </button>
      <h1 className="mb-2 text-2xl font-bold">{t('account.title')}</h1>
      <p className="mb-8 text-sm text-text-muted">
        {t('account.memberSince', { date: createdDate })}
      </p>

      {/* Section Tabs */}
      <div className="mb-6 flex flex-wrap gap-2">
        {sectionBtn('profile', t('account.profile'))}
        {sectionBtn('email', t('account.changeEmail'))}
        {sectionBtn('password', t('account.changePassword'))}
        {sectionBtn('address', t('account.changeAddress'))}
      </div>

      {/* Feedback */}
      {error && (
        <div className="mb-4 rounded-lg bg-error/10 p-3 text-sm text-error">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 rounded-lg bg-success/10 p-3 text-sm text-success">
          {success}
        </div>
      )}

      {/* Profile Section */}
      {section === 'profile' && (
        <div className="rounded-xl bg-surface-light p-6">
          <div className="flex flex-col gap-3">
            <div className="flex justify-between">
              <span className="text-text-muted">{t('auth.username')}</span>
              <span className="font-medium">{user.username}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-muted">{t('auth.email')}</span>
              <span className="font-medium">{user.email}</span>
            </div>
            {user.street && (
              <div className="flex justify-between">
                <span className="text-text-muted">{t('account.street')}</span>
                <span className="font-medium">{user.street}</span>
              </div>
            )}
            {(user.zip || user.city) && (
              <div className="flex justify-between">
                <span className="text-text-muted">{t('account.city')}</span>
                <span className="font-medium">
                  {[user.zip, user.city].filter(Boolean).join(' ')}
                </span>
              </div>
            )}
            {user.country && (
              <div className="flex justify-between">
                <span className="text-text-muted">{t('account.country')}</span>
                <span className="font-medium">{user.country}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Change Email */}
      {section === 'email' && (
        <form onSubmit={handleEmailChange} className="flex flex-col gap-4">
          <p className="text-sm text-text-muted">
            {t('auth.email')}: <span className="text-text">{user.email}</span>
          </p>
          <input
            type="email"
            placeholder={t('account.newEmail')}
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
            className="rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
          />
          <button
            type="submit"
            className="rounded-lg bg-primary py-3 font-medium text-white hover:bg-primary-dark transition-colors"
          >
            {t('account.save')}
          </button>
        </form>
      )}

      {/* Change Password */}
      {section === 'password' && (
        <form onSubmit={handlePasswordChange} className="flex flex-col gap-4">
          <input
            type="password"
            placeholder={t('account.currentPassword')}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            className="rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
          />
          <input
            type="password"
            placeholder={t('account.newPassword')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={6}
            className="rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
          />
          <input
            type="password"
            placeholder={t('account.confirmNewPassword')}
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            required
            minLength={6}
            className="rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
          />
          {newPassword && confirmNewPassword && newPassword !== confirmNewPassword && (
            <p className="text-xs text-error">{t('auth.passwordMismatch')}</p>
          )}
          <button
            type="submit"
            disabled={newPassword !== confirmNewPassword}
            className="rounded-lg bg-primary py-3 font-medium text-white hover:bg-primary-dark transition-colors disabled:opacity-50"
          >
            {t('account.save')}
          </button>
        </form>
      )}

      {/* Change Address */}
      {section === 'address' && (
        <form onSubmit={handleAddressChange} className="flex flex-col gap-4">
          <input
            type="text"
            placeholder={t('account.street')}
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            className="rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
          />
          <div className="flex gap-3">
            <input
              type="text"
              placeholder={t('account.zip')}
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              className="w-1/3 rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
            />
            <input
              type="text"
              placeholder={t('account.city')}
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="flex-1 rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
            />
          </div>
          <input
            type="text"
            placeholder={t('account.country')}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
          />
          <button
            type="submit"
            className="rounded-lg bg-primary py-3 font-medium text-white hover:bg-primary-dark transition-colors"
          >
            {t('account.save')}
          </button>
        </form>
      )}
    </div>
  )
}
